/**
 * Rétroplanning d'un projet — refonte UX, chantier 5 (maquette 8, validée le 24/09).
 *
 * Les paliers posés sur une frise de mois, la ligne « Aujourd'hui » et le jour J
 * (date pivot). Données RÉELLES du projet : date butoir de chaque palier (les
 * dates saisies font foi, mig 245), atteint / en cours / en retard. Rendu en
 * liste accessible : chaque palier est une ligne lisible au lecteur d'écran ;
 * la frise n'est qu'une aide visuelle.
 */
import { useNavigate } from 'react-router-dom';
import type { ProjetDetail } from '@/lib/api';

const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const jour = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();
const fr = (iso: string) => { const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`); return `${d.getUTCDate()} ${MOIS[d.getUTCMonth()]}`; };
const JOUR_MS = 86_400_000;

export function Retroplanning({ projet }: { projet: ProjetDetail }) {
  const navigate = useNavigate();
  // « Rédiger l'arrêté » : pour un palier non atteint qui parle d'un arrêté, un
  // arrêté PRÉREMPLI (titre, objet avec le projet, le lieu et les dates d'occupation).
  const redigerArrete = (libelle: string) => {
    const lieu = projet.lieuNom ? ` — ${projet.lieuNom}` : '';
    const occ = projet.occupation ? ` — du ${projet.occupation.dateDebut.split('-').reverse().join('/')} au ${projet.occupation.dateFin.split('-').reverse().join('/')}` : '';
    const q = new URLSearchParams({ titre: libelle, objet: `${libelle} — ${projet.intitule}${lieu}${occ}` });
    navigate(`/service-technique/arretes/nouveau?${q.toString()}`);
  };
  const dates = projet.paliers.filter((p) => p.dateButoir).map((p) => jour(p.dateButoir!));
  if (dates.length === 0) {
    return <p className="text-sm text-ink-muted">Saisissez les dates des paliers pour voir le rétroplanning.</p>;
  }
  const auj = jour(new Date().toISOString());
  const pivot = projet.datePivot ? jour(projet.datePivot) : null;
  const tous = [...dates, auj, ...(pivot ? [pivot] : [])];
  // Du 1er du premier mois au dernier jour du dernier mois : des repères lisibles.
  const d0 = new Date(Math.min(...tous)); const debut = Date.UTC(d0.getUTCFullYear(), d0.getUTCMonth(), 1);
  const d1 = new Date(Math.max(...tous)); const fin = Date.UTC(d1.getUTCFullYear(), d1.getUTCMonth() + 1, 1);
  const pct = (t: number) => ((t - debut) / (fin - debut)) * 100;
  const mois: Array<{ t: number; lib: string }> = [];
  for (let m = new Date(debut); m.getTime() < fin; m = new Date(Date.UTC(m.getUTCFullYear(), m.getUTCMonth() + 1, 1))) {
    mois.push({ t: m.getTime(), lib: `${MOIS[m.getUTCMonth()]} ${m.getUTCFullYear()}` });
  }
  const etatPalier = (p: ProjetDetail['paliers'][number]) =>
    p.atteint ? { lib: 'Fait', cls: 'bg-success', badge: 'bg-success/15 text-success' }
      : p.enRetard ? { lib: 'En retard', cls: 'bg-urgent', badge: 'bg-urgent/15 text-urgent' }
        : p.courant ? { lib: 'En cours', cls: 'bg-warning', badge: 'bg-warning/15 text-warning' }
          : { lib: 'À venir', cls: 'bg-tech', badge: 'bg-tech/15 text-tech-fg' };
  const relatif = (t: number) => {
    if (!pivot) return '';
    const j = Math.round((t - pivot) / JOUR_MS);
    return j === 0 ? ' · jour J' : ` · J${j > 0 ? '+' : ''}${j}`;
  };

  return (
    <div className="overflow-x-auto">
      <div className="relative min-w-[640px] pb-2 pl-[220px] pt-8">
        {/* Grille des mois */}
        <div className="pointer-events-none absolute inset-y-0 left-[220px] right-0" aria-hidden="true">
          {mois.map((m) => (
            <div key={m.t} className="absolute inset-y-0 border-l border-border/60" style={{ left: `${pct(m.t)}%` }}>
              <span className="absolute -top-0.5 ml-1.5 whitespace-nowrap text-2xs text-ink-subtle">{m.lib}</span>
            </div>
          ))}
          <div className="absolute inset-y-0 border-l-2 border-urgent" style={{ left: `${pct(auj)}%` }}>
            <span className="absolute top-3 ml-1 rounded-r-pill bg-urgent/15 px-1.5 text-2xs font-medium text-urgent">Aujourd’hui</span>
          </div>
          {pivot && <div className="absolute inset-y-0 border-l-2 border-dashed border-grc" style={{ left: `${pct(pivot)}%` }} />}
        </div>
        <ol aria-label="Paliers du projet">
          {[...projet.paliers].sort((a, b) => a.ordre - b.ordre).map((p) => {
            const e = etatPalier(p);
            return (
              <li key={p.etatId} className="relative flex h-14 items-center border-b border-border/50">
                <div className="absolute -left-[220px] w-[208px] pr-2">
                  <p className="truncate text-sm font-medium text-ink" title={p.libelle}>{p.libelle}</p>
                  {!p.atteint && /arr[êe]t[ée]/i.test(p.libelle) && (
                    <button type="button" onClick={() => redigerArrete(p.libelle)} className="text-2xs font-medium text-accent hover:underline">
                      Rédiger l’arrêté
                    </button>
                  )}
                  <p className="text-2xs text-ink-muted">{p.dateButoir ? `${fr(p.dateButoir)}${relatif(jour(p.dateButoir))}` : 'Date à saisir'}</p>
                </div>
                {p.dateButoir && (<>
                  <span aria-hidden="true" className={`absolute h-3.5 w-3.5 -translate-x-1/2 rotate-45 rounded-[3px] ${e.cls}`} style={{ left: `${pct(jour(p.dateButoir))}%` }} />
                  <span className={`absolute ml-3 whitespace-nowrap rounded-r-pill px-2 py-0.5 text-2xs font-medium ${e.badge}`} style={{ left: `${pct(jour(p.dateButoir))}%` }}>{e.lib}</span>
                </>)}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
